import type { QueryClient } from "@tanstack/react-query";
import { currentYear } from "constants/constants";
import { lazy } from "react";
import { getBusinessAdmins } from "services/businessAdministratorsServices";
import { getChartsRequest } from "services/chartServices";
import {
	getClientFinanceSummaryRequest,
	getClientsOverviewRequest,
} from "services/clientsServices/services";
import { getOverviewDataRequest } from "services/overviewServices";
import { getSchedulesPageDataRequest } from "services/scheduleServices/services";
import { getTranslators } from "services/translatorsServices/services";
import {
	getBalanceTotalForCurrentMonthRequest,
	getBalanceTotalForPreviousMonthRequest,
} from "../../services/balanceDayServices";
import { getManClientAnalyticsRequest } from "../../services/manClientAnalyticsServices";
import ChartPageSkeleton from "../Charts/ChartPageSkeleton";
import ClientInfoPageSkeleton from "../ClientInfo/ClientInfoPageSkeleton";
import FinanceStatementPageSkeleton from "../FinanceStatementPage/FinanceStatementPageSkeleton";
import ClientsPageSkeleton from "../ListOfClients/ClientsPageSkeleton";
import LiveDashboardSkeleton from "../LiveDashboard/LiveDashboardSkeleton";
import OverviewPageSkeleton from "../Overview/OverviewPageSkeleton";
import SchedulePageSkeleton from "../SchedulePage/SchedulePageSkeleton";
import TranslatorsPageSkeleton from "../Translators/TranslatorsPageSkeleton";

export interface RouteItem {
	component?: React.ComponentType;
	/** Shown in the tab, before " · Sunrise agency". */
	title?: string;
	fallback?: React.ReactNode;
	path: string;
	exact?: boolean;
	onLoad?: (
		queryClient: QueryClient,
		params: Record<string, string | undefined>,
	) => void | Promise<void>;
}

const FinanceStatementPage = lazy(
	() => import("../FinanceStatementPage/FinanceStatementPage"),
);
const AuthorizationPage = lazy(
	() => import("../AuthorizationPage/AuthorizationPage"),
);
const Overview = lazy(() => import("../Overview/Overview"));
const LiveDashboard = lazy(() => import("../LiveDashboard"));
const Translators = lazy(() => import("../Translators/Translators"));
const ListOfClients = lazy(() => import("../ListOfClients/ListOfClients"));
const ChartPage = lazy(() => import("../Charts/ChartPage"));
const ResetPasswordPage = lazy(
	() => import("../AuthorizationPage/ResetPasswordPage/ResetPasswordPage"),
);
const LoggedOutPage = lazy(
	() => import("../AuthorizationPage/LoggedOutPage/LoggedOutPage"),
);
const BusinessAdminsPage = lazy(() => import("../BusinessAdminsPage"));
const SchedulePage = lazy(() => import("../SchedulePage/SchedulePage"));
const ManClientAnalyticsPage = lazy(
	() => import("../ManClientAnalytics/ManClientAnalyticsPage"),
);
const ClientInfoPage = lazy(() => import("../ClientInfo/ClientInfoPage"));

export const privateRoutes: RouteItem[] = [
	{
		component: FinanceStatementPage,
		fallback: <FinanceStatementPageSkeleton />,
		path: "/finances",
		title: "Finances",
		exact: true,
		onLoad: (queryClient) => {
			queryClient.prefetchQuery({
				queryKey: ["currentMonthTotal"],
				queryFn: getBalanceTotalForCurrentMonthRequest,
			});
			queryClient.prefetchQuery({
				queryKey: ["previousMonthTotal"],
				queryFn: getBalanceTotalForPreviousMonthRequest,
			});
		},
	},
	{
		component: BusinessAdminsPage,
		path: "/business-admins",
		title: "Admins",
		exact: true,
		onLoad: (queryClient) => {
			queryClient.prefetchQuery({
				queryKey: ["businessAdministratorsQuery"],
				queryFn: () =>
					getBusinessAdmins({ searchQuery: "" }).then((r) => r.body),
			});
		},
	},
];

export const publicRoutes: RouteItem[] = [
	{
		component: ManClientAnalyticsPage,
		path: "/man-client-analytics",
		title: "Dating",
		exact: true,
		onLoad: (queryClient) => {
			const queryParams = {
				startDate: undefined,
				endDate: undefined,
				manId: "",
				clientName: "",
				limit: 50,
				skip: 0,
				sortBy: "totalRevenue:desc",
			};
			queryClient.prefetchQuery({
				queryKey: ["manClientAnalytics", queryParams],
				queryFn: () =>
					getManClientAnalyticsRequest(queryParams).then((res) => res.body),
				staleTime: 5 * 60 * 1000,
			});
		},
	},
	{
		component: SchedulePage,
		fallback: <SchedulePageSkeleton />,
		path: "/schedule",
		title: "Schedule",
		exact: true,
		onLoad: (queryClient) => {
			queryClient.prefetchQuery({
				queryKey: ["schedulesPageData"],
				queryFn: getSchedulesPageDataRequest,
			});
		},
	},
	{
		path: "/overview",
		title: "Overview",
		component: Overview,
		fallback: <OverviewPageSkeleton />,
		exact: true,
		onLoad: (queryClient) => {
			queryClient.prefetchQuery({
				queryKey: ["overviewData"],
				queryFn: async () => {
					const response = await getOverviewDataRequest({
						selectedYear: currentYear,
					});
					if (response.status !== 200) {
						throw new Error("Something went wrong with overview data");
					}
					return response.body;
				},
				staleTime: 5 * 60 * 1000,
			});
		},
	},
	{
		path: "/translators",
		title: "Translators",
		component: Translators,
		fallback: <TranslatorsPageSkeleton />,
		exact: true,
		onLoad: (queryClient) => {
			// The page reads ["translatorsForTranslators", hideSuspended] and starts
			// with "hide suspended" on, so the key and the params must both match or
			// the prefetch lands in a cache entry nobody reads and the page issues a
			// second, heavier request for the full list.
			queryClient.prefetchQuery({
				queryKey: ["translatorsForTranslators", true],
				queryFn: () =>
					getTranslators({ shouldGetClients: true, hideSuspended: true }).then(
						(r) => r.body,
					),
			});
		},
	},
	{
		path: "/clients",
		title: "Clients",
		component: ListOfClients,
		fallback: <ClientsPageSkeleton />,
		exact: true,
		onLoad: (queryClient) => {
			queryClient.prefetchQuery({
				queryKey: ["clientsOverview"],
				queryFn: getClientsOverviewRequest,
			});
		},
	},
	{
		path: "/clients/:id",
		title: "Client",
		component: ClientInfoPage,
		fallback: <ClientInfoPageSkeleton />,
		exact: true,
		onLoad: (queryClient, params) => {
			const id = params.id;
			if (id) {
				queryClient.prefetchQuery({
					queryKey: ["clientFinance", id],
					queryFn: () => getClientFinanceSummaryRequest(id),
				});
			}
		},
	},
	{
		path: "/chart",
		title: "Charts",
		component: ChartPage,
		fallback: <ChartPageSkeleton />,
		exact: true,
		onLoad: (queryClient) => {
			// Prefetch default charts (last months, all categories, all translators)
			queryClient.prefetchQuery({
				queryKey: [
					"chartsData",
					currentYear,
					"allCategories",
					"all",
					undefined,
				],
				queryFn: () =>
					getChartsRequest({
						yearFilter: currentYear,
						monthFilter: undefined,
						category: undefined,
						translatorId: undefined,
						clientId: undefined,
					}).then((r) => r.body),
			});
		},
	},
	{
		path: "/live-dashboard",
		title: "Live Dashboard",
		component: LiveDashboard,
		fallback: <LiveDashboardSkeleton />,
		exact: true,
		// No prefetch: query key uses Dayjs object instance, can't match from loader
	},
	{
		path: "/",
		component: AuthorizationPage,
		exact: true,
	},
	{
		path: "/logged-out",
		title: "Signed out",
		component: LoggedOutPage,
		exact: true,
	},
	{
		// Where the reset email points. Reachable signed out by definition — the
		// whole point is that whoever opens it cannot get in.
		path: "/reset-password",
		title: "Choose a new password",
		component: ResetPasswordPage,
		exact: true,
	},
];
